import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  clearOwnerSession,
  getOwnerSessionExpiry,
  isOwnerSessionExpired,
  saveOwnerSession,
} from "@/lib/authSession";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("sa_token");
    const raw = localStorage.getItem("sa_user");
    if (token && raw && !isOwnerSessionExpired()) {
      try {
        setUser(JSON.parse(raw));
      } catch {
        clearOwnerSession();
      }
    } else {
      clearOwnerSession();
    }
    setReady(true);
  }, []);

  const login = (token, u) => {
    saveOwnerSession(token, u);
    setUser(u);
  };

  const logout = useCallback(() => {
    clearOwnerSession();
    setUser(null);
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const expireIfNeeded = () => {
      if (isOwnerSessionExpired()) logout();
    };
    const expiry = getOwnerSessionExpiry();
    const timer = window.setTimeout(
      logout,
      Math.max(0, (expiry || 0) - Date.now())
    );

    window.addEventListener("focus", expireIfNeeded);
    window.addEventListener("pageshow", expireIfNeeded);
    window.addEventListener("owner-session-expired", logout);
    document.addEventListener("visibilitychange", expireIfNeeded);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", expireIfNeeded);
      window.removeEventListener("pageshow", expireIfNeeded);
      window.removeEventListener("owner-session-expired", logout);
      document.removeEventListener("visibilitychange", expireIfNeeded);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, ready, isAuthenticated: !!user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
